/**
 * 69.83: the kill feed's lines — who killed whom and by what kind of kill, from a tick's events (the owner: "for dropship kill
 * it'll say "? killed x", also we don't see what type of kill it was, would be cool to show small arms vs mortar vs dropship").
 *
 * A kill names a person when one fired it. A side's own sources have no person — their owner is `-1 - team` (core/airstrike.ts
 * airstrikeOwner: the gunship's rounds, a helicopter's, the guns' and the mortar's strike rounds) and a vehicle's death burst
 * is `-1000 - its id` (core/explosions.ts vehicleBlastOwner) — so the line names the source by what fired it: the round's ship
 * (`fromShip`, a helicopter's), the mortar's tag (`by`), the burst's class. Deaths no kill event names come from the ship and
 * airborne events: riders lost with their ship (a crash), people under a falling ship (the dropship), a vehicle's crew lost
 * with its hull (69.89: by the blow that wrecked it), a fall, the star, a wormhole or deep space (with its launcher when one
 * is credited). Pure: the host adds the names it shows and draws the line.
 */
import type { GameState, StructureType } from '../types';
import type { ExplosionClass } from '../constants/explosions';
import type { ActionEvent, AirborneEvent } from '../match/match';

export type KillKind = 'smallArms' | 'mountedGun' | 'grenade' | 'rocket' | 'mortar' | 'artillery' | 'airstrike' | 'gunship' | 'helicopter'
  | 'dropship' | 'vehicle' | 'crash' | 'fall' | 'charge';   // (69.47: the engineer's placed charge)

/** The tag a line shows for its kind. */
export const KILL_KIND_TAG: Record<KillKind, string> = {
  smallArms: 'small arms', mountedGun: 'mounted gun', grenade: 'grenade', rocket: 'rocket', mortar: 'mortar', artillery: 'artillery',
  airstrike: 'airstrike', gunship: 'gunship', helicopter: 'helicopter', dropship: 'dropship', vehicle: 'vehicle', crash: 'crash', fall: 'fall', charge: 'charge',
};

/** One kill: the killer (a person's id, or a side's source by name), its side (null: none), the victim and the kind. */
export interface KillLine { killerId?: number; killer: string; killerTeam: number | null; victimId: number; kind: KillKind }

const VEHICLES: ReadonlySet<StructureType> = new Set<StructureType>(['motorcycle', 'wheeled', 'technical', 'supplyTruck', 'tracked', 'tank']);

/** A person's burst by its class (a grenade, a rocket, a vehicle's gun, a drone's charge, a shell). */
function burstKind(x: ExplosionClass): KillKind {
  switch (x) {
    case 'handGrenade': case 'gl40': return 'grenade';
    case 'law': case 'panzerfaust': return 'rocket';
    case 'tankHe': case 'cannon20': case 'vehicleDeath': return 'vehicle';
    case 'strikeCharge': return 'airstrike';
    case 'charge': return 'charge';   // 69.47: the engineer's placed charge
    default: return 'artillery';   // shell, heavyShell, bomb
  }
}

/** A side's source (owner -1 - team) by what fired it. */
function sideSource(how: { fromShip?: number; by?: 'mortar'; explosion?: ExplosionClass }): { killer: string; kind: KillKind } {
  if (how.by === 'mortar') return { killer: 'Mortar', kind: 'mortar' };
  if (how.explosion === 'bomb') return { killer: 'Artillery', kind: 'artillery' };
  if (how.fromShip !== undefined) return { killer: 'Helicopter', kind: 'helicopter' };
  return { killer: 'Gunship', kind: 'gunship' };
}

/**
 * The kill lines a tick's (or a frame's) events make, one per victim, in the events' order — the action events, or an airborne
 * step's (a fall). `state` is the match after them (the dead are still in its players).
 */
export function killLines(state: Pick<GameState, 'players' | 'structures'>, events: ReadonlyArray<ActionEvent | AirborneEvent>): KillLine[] {
  const out: KillLine[] = [], seen = new Set<number>();
  const person = (id: number) => state.players.find(p => p.id === id);
  const add = (victimId: number, line: Omit<KillLine, 'victimId'>) => { if (!seen.has(victimId)) { seen.add(victimId); out.push({ ...line, victimId }); } };
  /** a kill by `ownerId`: a person by the kind `kindOf` gives, a side's source, or a vehicle's death burst */
  const byOwner = (victimId: number, ownerId: number, how: { fromShip?: number; by?: 'mortar'; explosion?: ExplosionClass }, kindOf: (p: GameState['players'][number]) => KillKind) => {
    if (ownerId >= 0) {
      const p = person(ownerId);
      add(victimId, { killerId: ownerId, killer: p?.name ?? '?', killerTeam: p?.teamIndex ?? null, kind: p ? kindOf(p) : 'smallArms' });
    } else if (ownerId > -1000) add(victimId, { ...sideSource(how), killerTeam: -1 - ownerId });
    else {
      const s = state.structures.find(x => x.id === -1000 - ownerId), o = s ? person(s.ownerId) : undefined;
      add(victimId, { killer: 'Wreck', killerTeam: o?.teamIndex ?? null, kind: 'vehicle' });
    }
  };
  /** a person's gun: the seat's (a vehicle's or a mount's) or their own */
  const gunKind = (p: GameState['players'][number]): KillKind => {
    if (!p.mount) return 'smallArms';
    const s = state.structures.find(x => x.id === p.mount!.bodyId);
    return s && VEHICLES.has(s.type) ? 'vehicle' : 'mountedGun';
  };
  const credits = new Map<number, number>();   // victim → the launcher credited with a fall
  for (const e of events) if (e.kind === 'killCredit') credits.set(e.victimId, e.killerId);
  for (const e of events) {
    if (e.kind === 'hitUnit' && e.killed) {
      const b = e.bullet, x = b.explosion;
      byOwner(e.targetId, b.ownerId, { fromShip: b.fromShip, by: b.by, explosion: x }, p => (b.blast && x ? burstKind(x) : gunKind(p)));
    } else if (e.kind === 'blast') {
      for (const v of e.kills) byOwner(v, e.ownerId, e, () => burstKind(e.explosion));
    } else if (e.kind === 'shellImpact') {
      for (const v of e.resolution.kills) byOwner(v, e.shell.ownerId, { explosion: 'shell' }, () => 'artillery');
    } else if (e.kind === 'shipCrashed') {
      const fell = e.craft === 'heli' ? { killer: 'Helicopter', kind: 'helicopter' as const } : e.craft === 'gunship' ? { killer: 'Gunship', kind: 'gunship' as const } : { killer: 'Dropship', kind: 'dropship' as const };   // (69.83's open line: what came down)
      for (const im of e.impacts ?? []) for (const v of im.kills) add(v, { ...fell, killerTeam: e.team });
      for (const v of e.lost) add(v, { killer: 'Crash', killerTeam: e.team, kind: 'crash' });
    } else if (e.kind === 'shipLost') {
      for (const v of e.lost) add(v, { killer: 'Crash', killerTeam: e.team, kind: 'crash' });
    } else if (e.kind === 'crewLost') {
      // 69.89: a crew lost with its hull — by the blow that wrecked it, as that blow's own kill would read; a falling ship's
      // the dropship's; none found, the wreck's
      const by = e.by;
      for (const v of e.lost) {
        if (by) byOwner(v, by.ownerId, by, p => (by.explosion ? burstKind(by.explosion) : gunKind(p)));
        else if (e.crashed !== undefined) add(v, { killer: 'Dropship', killerTeam: e.crashed, kind: 'dropship' });
        else add(v, { killer: 'Wreck', killerTeam: null, kind: 'vehicle' });
      }
    }
  }
  for (const e of events) {
    // (a launch's end: a fall, the star, a wormhole, deep space — the launcher's when one is credited)
    if ((e.kind === 'landed' && e.died) || e.kind === 'diedInStar' || e.kind === 'diedInWormhole' || e.kind === 'lostInSpace') {
      const by = credits.get(e.playerId), p = by !== undefined ? person(by) : undefined;
      const where = e.kind === 'lostInSpace' ? 'Space' : e.kind === 'diedInWormhole' ? 'Wormhole' : 'Fall';
      add(e.playerId, p ? { killerId: p.id, killer: p.name, killerTeam: p.teamIndex, kind: 'fall' } : { killer: where, killerTeam: null, kind: 'fall' });
    }
  }
  return out;
}
